import { DEBT_TYPE, FREQUENCY } from '@funnels/constants';
import {
  formatToPrecision,
  getAnnualValueFromFrequency,
  getDefaultInterestRate,
  getNumOfRepayments,
  getRateForFrequency,
} from '@funnels/utils';

/**
 * Calculate the loan interest rate
 *
 * @param balance: The balance remaining
 * @param loanTerm: The period of loan in months
 * @param repayment: the repayment to be made every installment
 * @param repaymentFrequency: Default monthly. Repayment frequency. The amount returned corresponds to this frequency
 * @param interestOnlyPeriod: Interest only period, if loan type is interest only
 * @param debtType: the type of the debt
 */
export const calculateInterestRate = (
  balance?: number,
  loanTerm?: number,
  repayment?: number,
  repaymentFrequency?: FREQUENCY,
  interestOnlyPeriod?: number,
  debtType: DEBT_TYPE = DEBT_TYPE.MORTGAGE
) => {
  if (!balance || !loanTerm || !repayment || !repaymentFrequency) {
    return;
  }

  if (interestOnlyPeriod) {
    return formatToPrecision(getAnnualValueFromFrequency((repayment * 100) / balance, repaymentFrequency));
  }

  let calcRepayment = 0;
  // Convert annual interest rate to interest rate for given repayment frequency and change from percentage to decimal
  // prettier-ignore
  let interestRate = (getRateForFrequency(getDefaultInterestRate(debtType), repaymentFrequency) / 100);
  const numRepayments = getNumOfRepayments(loanTerm, repaymentFrequency);
  while (formatToPrecision(calcRepayment) !== formatToPrecision(repayment)) {
    interestRate =
      interestRate -
      (repayment - repayment * Math.pow(1 + interestRate, -numRepayments) - interestRate * balance) /
        (numRepayments * repayment * Math.pow(1 + interestRate, -(numRepayments + 1)) - balance);
    if (interestRate < 0 || isNaN(interestRate)) {
      return 0;
    }
    calcRepayment = (interestRate * balance) / (1 - Math.pow(1 + interestRate, -numRepayments));
  }

  return formatToPrecision(getAnnualValueFromFrequency(interestRate, repaymentFrequency) * 100);
};
